import { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';
import { Button, ActionIcon, NumberInput, Text, Group } from '@mantine/core';
import { IconPlus, IconTrash, IconCheck } from '@tabler/icons-react';
import './AdminProducts.css';
import './ShippingListSection.css';

let tierKeyCounter = 0;
function tierKey() {
  tierKeyCounter += 1;
  return `tier-${Date.now()}-${tierKeyCounter}`;
}

export default function ShippingTiersSection() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [newMin, setNewMin] = useState('');
  const [newCost, setNewCost] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const rows = await apiService.getShippingTiers();
      setTiers(rows.map((r) => ({ key: tierKey(), minSubtotal: Number(r.minSubtotal), cost: Number(r.cost) })));
    } catch (err) {
      setError(err.message || 'Failed to load shipping tiers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Mirrors the backend validation (backend remains authoritative).
  const validate = (rows) => {
    if (rows.length === 0) return 'At least one shipping tier is required.';
    const seen = new Set();
    for (const t of rows) {
      const min = Number(t.minSubtotal);
      const cost = Number(t.cost);
      if (!Number.isFinite(min) || min < 0) return 'Min order values must be non-negative numbers.';
      if (!Number.isFinite(cost) || cost < 0) return 'Shipping costs must be non-negative numbers.';
      if (seen.has(min)) return 'Min order values must be unique.';
      seen.add(min);
    }
    if (!seen.has(0)) return 'A tier with min order value 0.00 is required as the fallback.';
    return null;
  };

  const handleAdd = () => {
    const min = Number(newMin);
    const cost = Number(newCost);
    if (newMin === '' || newCost === '' || !Number.isFinite(min) || min < 0 || !Number.isFinite(cost) || cost < 0) {
      setError('Enter valid non-negative numbers for both fields.');
      return;
    }
    if (tiers.some((t) => Number(t.minSubtotal) === min)) {
      setError('A tier with this min order value already exists.');
      return;
    }
    setError('');
    setSaved(false);
    setDirty(true);
    setTiers((prev) =>
      [...prev, { key: tierKey(), minSubtotal: min, cost }]
        .sort((a, b) => Number(b.minSubtotal) - Number(a.minSubtotal))
    );
    setNewMin('');
    setNewCost('');
  };

  const handleChange = (key, field, value) => {
    setError('');
    setSaved(false);
    setDirty(true);
    setTiers((prev) => prev.map((t) => (t.key === key ? { ...t, [field]: value } : t)));
  };

  const handleRemove = (key) => {
    setError('');
    setSaved(false);
    setDirty(true);
    setTiers((prev) => prev.filter((t) => t.key !== key));
  };

  const handleSave = async () => {
    const problem = validate(tiers);
    if (problem) { setError(problem); return; }
    setSaving(true);
    setError('');
    try {
      await apiService.updateShippingTiers(tiers.map((t) => ({ minSubtotal: Number(t.minSubtotal), cost: Number(t.cost) })));
      setDirty(false);
      setSaved(true);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to save shipping tiers');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setError('');
    setSaved(false);
    setDirty(false);
    load();
  };

  return (
    <div className="settings-section">
      <div className="settings-section-header">
        <h2 className="settings-section-title">Shipping Costs</h2>
        <p className="settings-section-description">
          Delivery cost tiers by order value. <strong>Costs are stored net (excl. Swiss VAT / MWST)</strong> — enter net amounts; the
          customer is charged the VAT-inclusive cost. The first tier whose min order value the discounted subtotal meets wins.
        </p>
      </div>

      <form
        className="settings-add-row"
        onSubmit={(e) => { e.preventDefault(); handleAdd(); }}
        aria-label="Add shipping tier"
      >
        <NumberInput
          placeholder="Min. order value"
          value={newMin}
          onChange={(v) => { setNewMin(v); setError(''); setSaved(false); }}
          min={0}
          decimalScale={2}
          prefix="CHF "
          flex={1}
          aria-label="Minimum order value of the new tier"
          hideControls
        />
        <NumberInput
          placeholder="Shipping cost (excl. VAT)"
          value={newCost}
          onChange={(v) => { setNewCost(v); setError(''); setSaved(false); }}
          min={0}
          decimalScale={2}
          prefix="CHF "
          flex={1}
          aria-label="Shipping cost of the new tier (excl. VAT)"
          hideControls
        />
        <Button
          type="submit"
          color="green"
          leftSection={<IconPlus size={16} aria-hidden="true" />}
          disabled={newMin === '' || newCost === ''}
        >
          Add tier
        </Button>
      </form>

      {error && (
        <Text id="shipping-tiers-error" role="alert" c="red" size="sm" mt="xs">
          {error}
        </Text>
      )}
      {saved && (
        <Text id="shipping-tiers-saved" role="status" c="green" size="sm" mt="xs">
          Shipping costs saved — active immediately.
        </Text>
      )}

      {loading ? (
        <p className="settings-section-loading" role="status" aria-live="polite">Loading...</p>
      ) : (
        <div className="table-responsive" style={{ marginTop: '1rem' }}>
          <table className="admin-table">
            <caption className="sr-only">Shipping cost tiers</caption>
            <thead>
              <tr>
                <th scope="col">Min. Order Value (CHF)</th>
                <th scope="col">Shipping Cost (CHF, excl. VAT)</th>
                <th scope="col" style={{ width: 60 }}>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {tiers.map((tier) => (
                <tr key={tier.key}>
                  <td>
                    <NumberInput
                      value={tier.minSubtotal}
                      onChange={(v) => handleChange(tier.key, 'minSubtotal', v)}
                      min={0}
                      decimalScale={2}
                      size="sm"
                      aria-label={`Minimum order value for tier ${tier.key}`}
                      hideControls
                      styles={{ input: { maxWidth: 200 } }}
                    />
                  </td>
                  <td>
                    <NumberInput
                      value={tier.cost}
                      onChange={(v) => handleChange(tier.key, 'cost', v)}
                      min={0}
                      decimalScale={2}
                      size="sm"
                      aria-label={`Shipping cost for tier ${tier.key}`}
                      hideControls
                      styles={{ input: { maxWidth: 200 } }}
                    />
                  </td>
                  <td>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => handleRemove(tier.key)}
                      title="Delete tier"
                      aria-label={`Delete tier with min order value ${tier.minSubtotal}`}
                    >
                      <IconTrash size={16} aria-hidden="true" />
                    </ActionIcon>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Group mt="md">
        <Button
          onClick={handleSave}
          loading={saving}
          disabled={!dirty || loading}
          leftSection={<IconCheck size={16} aria-hidden="true" />}
        >
          Save changes
        </Button>
        {dirty && !loading && (
          <Button variant="subtle" color="gray" onClick={handleDiscard}>
            Discard
          </Button>
        )}
      </Group>
    </div>
  );
}
